import { SseParser } from "./sse";
import type { RealtimeClientOptions, RealtimeStatus } from "./types";

/**
 * SSE client over fetch + ReadableStream (ADR-0012): native EventSource cannot set an
 * Authorization header, so the stream request reuses the exact Bearer flow of every other API
 * call. Reconnects with exponential backoff + jitter; sends `Last-Event-ID` on reconnect.
 */
export class RealtimeClient {
  private abort: AbortController | null = null;
  private stopped = true;
  private retryDelayMs: number;
  private lastEventId: string | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: RealtimeClientOptions) {
    this.retryDelayMs = options.minRetryDelayMs ?? 1_000;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.abort?.abort();
    this.setStatus("closed");
  }

  private async connect(): Promise<void> {
    this.setStatus("connecting");
    this.abort = new AbortController();
    try {
      const token = await this.options.getToken();
      const headers: Record<string, string> = { Accept: "text/event-stream" };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (this.lastEventId) headers["Last-Event-ID"] = this.lastEventId;

      const response = await fetch(this.options.url, { headers, signal: this.abort.signal });
      if (!response.ok || !response.body) {
        throw new Error(`realtime stream refused: HTTP ${response.status}`);
      }

      this.setStatus("open");
      this.retryDelayMs = this.options.minRetryDelayMs ?? 1_000; // successful open resets backoff

      const parser = new SseParser((event) => {
        if (event.lastEventId) this.lastEventId = event.lastEventId;
        this.options.onEvent(event);
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.feed(decoder.decode(value, { stream: true }));
      }
    } catch {
      // Aborted (stop()) or network failure — fall through to the retry decision below.
    }
    if (!this.stopped) this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.setStatus("connecting");
    const max = this.options.maxRetryDelayMs ?? 30_000;
    const jitter = 0.5 + Math.random(); // 0.5x–1.5x, de-syncs reconnect stampedes
    const delay = Math.min(this.retryDelayMs * jitter, max);
    this.retryDelayMs = Math.min(this.retryDelayMs * 2, max);
    this.retryTimer = setTimeout(() => void this.connect(), delay);
  }

  private setStatus(status: RealtimeStatus): void {
    this.options.onStatusChange?.(status);
  }
}
